import { usePostInspectionMutation } from "@/services/backend/inspections/service";
import ConfirmationDialog from "@/shared/components/domain/confirmation/confirmation-dialog";
import {
  useErrArtefactNotReady,
  useSetErrArtefactNotReadyFromRtk,
} from "@/shared/service-manager/artefact/err-artefact-not-ready";
import { Button } from "@/shared/components/ui/button";
import { ReactElement, useEffect } from "react";
import { v4 } from "uuid";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { useNavigate } from "react-router";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";

interface CreateInspectionButtonProps {
  processId: string;
  differentiationId: string;
  disabled?: boolean;
}

export default function CreateInspectionButton({
  processId,
  differentiationId,
  disabled,
}: CreateInspectionButtonProps): ReactElement {
  const nav = useNavigate();
  const canCreate = useFeatureAuth("inspections.inspection.create");
  const [createReport, { error, reset, isLoading, isSuccess, data }] =
    usePostInspectionMutation();
  const { resetNotReadyError } = useErrArtefactNotReady();
  useSetErrArtefactNotReadyFromRtk(error, () => {
    reset();
  });

  useEffect(() => {
    if (isSuccess && data != null && data.inspection != null) {
      nav(`/processes/${processId}/inspection/${data.inspection.id}/metadata`);
    }
  }, [isSuccess, data, nav, processId]);

  const { user } = useAuth();
  const onConfirm = () => {
    if (isLoading || !canCreate) {
      return;
    }

    resetNotReadyError();
    createReport({
      id: v4(),
      processId,
      differentiationId,
      createdBy: user?.id as string,
    });
  };

  return (
    <ConfirmationDialog
      trigger={
        <Button disabled={!canCreate || disabled}>
          <p>Inspektionsbericht erstellen</p>
        </Button>
      }
      title="Neuen Prüfbericht erstellen"
      description="Hiermit wird ein Bericht mit den aktuellen Daten des Vorgangs angelegt."
      onConfirm={onConfirm}
    />
  );
}
